import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { FileText, Calendar, UploadCloud, Trash2 } from 'lucide-react';

interface ImportRecord {
  id: string;
  filename: string;
  uploadedAt: any;
  addedCount: number;
  orphanCount: number;
}

interface Props {
  tenantId: string;
}

export const POSImportHistory: React.FC<Props> = ({ tenantId }) => {
  const [history, setHistory] = useState<ImportRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this import record? This will NOT delete the associated transaction data.')) {
      try {
        await deleteDoc(doc(db, 'tenants', tenantId, 'import_history', id));
        setHistory(history.filter(h => h.id !== id));
      } catch (error) {
        console.error("Error deleting import record:", error);
      }
    }
  };

  useEffect(() => {
    if (!tenantId) return;

    const fetchHistory = async () => {
      try {
        const q = query(collection(db, 'tenants', tenantId, 'import_history'), orderBy('uploadedAt', 'desc'));
        const snapshot = await getDocs(q);
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ImportRecord));
        setHistory(docs);
      } catch (error) {
        console.error("Error fetching import history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [tenantId]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-gray-900 uppercase tracking-tight">POS Import File History</h3>
      {loading ? (
        <div className="animate-pulse h-20 bg-gray-100 rounded-xl" />
      ) : (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-xs text-left">
            <thead className="bg-gray-50 text-gray-400 uppercase font-bold">
              <tr>
                <th className="p-4">Filename</th>
                <th className="p-4">Uploaded At</th>
                <th className="p-4 text-center">Added</th>
                <th className="p-4 text-center">Orphans</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {history.map(record => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="p-4 font-bold text-gray-900 flex items-center gap-2">
                    <FileText size={16} className="text-blue-500" />
                    {record.filename}
                  </td>
                  <td className="p-4 text-gray-500">{record.uploadedAt?.toDate().toLocaleString()}</td>
                  <td className="p-4 text-center font-black text-emerald-600">{record.addedCount}</td>
                  <td className="p-4 text-center font-black text-amber-600">{record.orphanCount}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => handleDelete(record.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
